import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ScheduledReportEmailDeliveryConfig,
  ScheduledReportRunDeliveryOverride,
} from '../../../services/scheduled-reports/scheduled-reports.service';

export interface PayrollReportRerunDialogData {
  reportName: string;
  timezone?: string | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  asOfAt?: string | null;
  deliveryChannel?: string | null;
  emailDeliveryConfig?: ScheduledReportEmailDeliveryConfig | null;
  allowDeliveryOverride?: boolean;
  isSensitive?: boolean;
}

export interface PayrollReportRerunDialogResult {
  windowStart: string;
  windowEnd: string;
  asOfAt: string;
  reason?: string;
  deliveryOverride?: ScheduledReportRunDeliveryOverride;
}

@Component({
    selector: 'app-payroll-report-rerun-dialog',
    templateUrl: './payroll-report-rerun-dialog.component.html',
    styleUrls: ['./payroll-report-rerun-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PayrollReportRerunDialogComponent {
  protected readonly form = this.formBuilder.group({
    windowStart: [this.toDateTimeLocalValue(this.data.windowStart), Validators.required],
    windowEnd: [this.toDateTimeLocalValue(this.data.windowEnd), Validators.required],
    asOfAt: [this.toDateTimeLocalValue(this.data.asOfAt), Validators.required],
    reason: [''],
    deliveryChannel: [this.data.allowDeliveryOverride === false ? 'saved' : 'email', Validators.required],
    to: ['', this.data.allowDeliveryOverride === false ? [] : [Validators.required, Validators.email]],
  });
  protected readonly displayTimezone = `${this.data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'}`;

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<PayrollReportRerunDialogComponent, PayrollReportRerunDialogResult>,
    @Inject(MAT_DIALOG_DATA) protected data: PayrollReportRerunDialogData,
  ) {
    this.form.get('deliveryChannel')?.valueChanges.subscribe((deliveryChannel) => {
      const toControl = this.form.get('to');
      if (`${deliveryChannel}` === 'email') {
        toControl?.setValidators([Validators.required, Validators.email]);
      } else {
        toControl?.clearValidators();
      }
      toControl?.updateValueAndValidity();
    });
  }

  protected cancel(): void {
    this.dialogRef.close();
  }

  protected submit(): void {
    const toControl = this.form.get('to');
    toControl?.setValue(`${toControl.value || ''}`.trim());

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const deliveryChannel = `${raw.deliveryChannel || 'saved'}`;
    const savedEmailConfig: ScheduledReportEmailDeliveryConfig = this.data.emailDeliveryConfig || { to: [] };
    const deliveryOverride: ScheduledReportRunDeliveryOverride | undefined = deliveryChannel === 'email'
      ? {
        deliveryChannel: 'email',
        deliveryConfig: {
          to: [`${raw.to || ''}`.trim()],
          cc: [],
          bcc: [],
          ...(`${savedEmailConfig.subjectTemplate || ''}`.trim()
            ? { subjectTemplate: `${savedEmailConfig.subjectTemplate}`.trim() }
            : {}),
          ...(`${savedEmailConfig.bodyTemplate || ''}`.trim()
            ? { bodyTemplate: `${savedEmailConfig.bodyTemplate}`.trim() }
            : {}),
          ...(savedEmailConfig.attachFile !== undefined
            ? { attachFile: savedEmailConfig.attachFile }
            : {}),
        },
      }
      : undefined;

    this.dialogRef.close({
      windowStart: this.toApiDateTime(raw.windowStart || ''),
      windowEnd: this.toApiDateTime(raw.windowEnd || ''),
      asOfAt: this.toApiDateTime(raw.asOfAt || ''),
      reason: `${raw.reason || ''}`.trim() || undefined,
      ...(deliveryOverride ? { deliveryOverride } : {}),
    });
  }

  private toDateTimeLocalValue(value?: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const hour = `${date.getHours()}`.padStart(2, '0');
    const minute = `${date.getMinutes()}`.padStart(2, '0');
    return `${year}-${month}-${day}T${hour}:${minute}`;
  }

  private toApiDateTime(value: string): string {
    return value ? value.replace('T', ' ') + ':00' : '';
  }
}
